import {ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewEncapsulation} from '@angular/core';
import {AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';

import {FuseConfigService} from '@fuse/services/config.service';
import {fuseAnimations} from '@fuse/animations';
import {ActivatedRoute, Router, RouterModule} from '@angular/router';
import {AccountService} from 'app/core/auth/account.service';
import {ToastrService} from 'ngx-toastr';
import {MatButtonModule} from '@angular/material/button';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatIconModule} from '@angular/material/icon';
import {MatInputModule} from '@angular/material/input';
import {FuseSharedModule} from '@fuse/shared.module';

@Component({
    // Angular 22 defaults to OnPush; Eager preserves the previous behaviour.
    changeDetection: ChangeDetectionStrategy.Eager,
    selector: 'reset-password',
    templateUrl: './reset-password.component.html',
    styleUrls: ['./reset-password.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: fuseAnimations,
    standalone: true,
    imports: [MatInputModule, MatButtonModule, MatFormFieldModule, MatIconModule, FuseSharedModule, RouterModule]
})
export class ResetPasswordComponent implements OnInit, OnDestroy {
    resetPasswordForm: UntypedFormGroup;
    key: string;
    tenant: string;

    // Private
    private _unsubscribeAll: Subject<void>;
    public is_dark_mode = false;

    constructor(
        private router: Router,
        private accountService: AccountService,
        private _toastr: ToastrService,
        private activeRoute: ActivatedRoute,
        private _formBuilder: UntypedFormBuilder,
        private _fuseConfigService: FuseConfigService
    ) {
        // Configure the layout
        this.is_dark_mode = localStorage.getItem('isDarkModeActive') === 'true';
        this._fuseConfigService.config = {
            layout: {
                navbar: {
                    hidden: true
                },
                toolbar: {
                    hidden: true
                },
                footer: {
                    hidden: true
                },
                sidepanel: {
                    hidden: true
                }
            }
        };

        // Set the private defaults
        this._unsubscribeAll = new Subject<void>();
        // activeRoute.data.subscribe(data => console.log(data));
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        this.getRouteParams();
        this.resetPasswordForm = this._formBuilder.group({
            name: [''],
            email: ['', [Validators.email]],
            password: ['', Validators.required],
            passwordConfirm: ['', [Validators.required, confirmPasswordValidator]]
        });

        // Update the validity of the 'passwordConfirm' field
        // when the 'password' field changes
        this.resetPasswordForm
            .get('password')
            .valueChanges.pipe(takeUntil(this._unsubscribeAll))
            .subscribe(() => {
                this.resetPasswordForm.get('passwordConfirm').updateValueAndValidity();
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    // tslint:disable-next-line:typedef
    getRouteParams() {
        const queryParams = this.activeRoute.snapshot.queryParams;

        this.activeRoute.fragment.subscribe((fragment: string) => {
            if (fragment != null) {
                // used if the url has a # in it
                const urlParams = new URLSearchParams(fragment.split('?')[1]);

                this.key = urlParams.get('key');
                this.tenant = urlParams.get('tenant');
            } else {
                // used when url has no hash in it
                this.key = queryParams.key;
                this.tenant = queryParams.tenant;
            }
        });
    }

    /**
     * Reset user's password
     */
    // tslint:disable-next-line:typedef
    resetPassword() {
        this.accountService.finishPasswordReset(this.tenant, this.key, this.resetPasswordForm.value.password).subscribe(
            (result) => {
                this._toastr.info('Password was successfully changed');
                setTimeout(() => {
                    this.router.navigate(['/login']);
                }, 3000);
            },
            (err) => {
                this._toastr.error('The was an error resetting your password, please contact HM support');
            }
        );
    }
}

/**
 * Confirm password validator
 *
 * @param {AbstractControl} control
 * @returns {ValidationErrors | null}
 */
export const confirmPasswordValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    if (!control.parent || !control) {
        return null;
    }

    const password = control.parent.get('password');
    const passwordConfirm = control.parent.get('passwordConfirm');

    if (!password || !passwordConfirm) {
        return null;
    }

    if (passwordConfirm.value === '') {
        return null;
    }

    if (password.value === passwordConfirm.value) {
        return null;
    }

    return {passwordsNotMatching: true};
};
